<template>
  <div class="flex flex-col gap-y-4 items-center justify-center">
    <nav
      v-if="meta.total > 0 && totalPages > 1"
      class="nami-pagination-wrap"
      aria-label="Pagination"
    >
      <button
        v-if="totalPages > MAX_DISPLAYED_PAGES"
        @click="$emit('update:modelValue', 1)"
        class="nami-pagination-button"
        :class="modelValue === 1 && 'disabled'"
      >
        <span class="sr-only">Start</span>
        <IconChevronsLeft aria-hidden="true" />
      </button>

      <button
        v-for="page in visiblePages"
        @click="$emit('update:modelValue', page)"
        class="nami-pagination-button"
        :class="modelValue === page && 'active'"
      >
        {{ page }}
      </button>

      <button
        v-if="totalPages > MAX_DISPLAYED_PAGES"
        @click="$emit('update:modelValue', totalPages)"
        class="nami-pagination-button"
        :class="modelValue === totalPages && 'disabled'"
      >
        <span class="sr-only">End</span>
        <IconChevronsRight aria-hidden="true" />
      </button>
    </nav>
    <p class="text-sm sm:text-base" v-if="meta.total && !hideTotal">
      {{
        $t("misc.result", {
          count: Math.min(meta.total, noLimit ? Infinity : 10000),
        })
      }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { IconChevronsRight, IconChevronsLeft } from "@tabler/icons-vue";
import type { Meta } from "~/src/api";

type MakeOptional<T extends Object, K extends keyof T> = Omit<T, K> & {
  [Key in K]?: T[K];
};

const props = defineProps<{
  modelValue: number;
  meta: Meta | MakeOptional<Meta, "offset">;
  hideTotal?: boolean;
  noLimit?: boolean;
}>();

defineEmits<{
  (e: "update:modelValue", v: number): void;
}>();

const MAX_DISPLAYED_PAGES = 5;
const IDEAL_ADJACENT_PAGES = Math.floor(MAX_DISPLAYED_PAGES / 2);

const totalPages = computed(() =>
  Math.ceil(props.meta.total / props.meta.limit),
);

const visiblePages = computed(() => {
  if (totalPages.value < MAX_DISPLAYED_PAGES) {
    return Array.range(1, totalPages.value + 1);
  }

  let centerPage = props.modelValue;

  if (props.modelValue - 1 < IDEAL_ADJACENT_PAGES) {
    centerPage += IDEAL_ADJACENT_PAGES - centerPage + 1;
  } else if (totalPages.value - centerPage < IDEAL_ADJACENT_PAGES) {
    centerPage -= IDEAL_ADJACENT_PAGES - totalPages.value + centerPage;
  }

  return Array.range(
    centerPage - IDEAL_ADJACENT_PAGES,
    centerPage + IDEAL_ADJACENT_PAGES + 1,
  );
});
</script>

<style scoped lang="postcss">
.nami-pagination-wrap {
  @apply isolate inline-flex -space-x-px rounded-md shadow-sm;

  & > :last-child {
    @apply rounded-r-md;
  }
  & > :first-child {
    @apply rounded-l-md;
  }
}

.nami-pagination-button {
  @apply relative inline-flex items-center justify-center h-10 w-10
  border border-neutral-300 dark:border-neutral-700
  text-sm font-medium
  bg-white dark:bg-neutral-800
  hover:opacity-75 active:opacity-50;

  &.active {
    @apply z-[3]
    text-nami-comi-blue
    dark:bg-neutral-700
    border-nami-comi-blue;
  }
}
</style>
